/**
 * @file Use Shift select hook.
 */

import { useState, useEffect } from 'react';

const SHIFT = 16;
const ACTION_TYPES = {
  SELECT: 'SELECT',
  DESELECT: 'DESELECT',
};

/**
 * Allows to multi-select items when holding shift.
 *
 * We need both methods (onSelect / onDeselect) so that we can properly select/deselect depending
 * on where the user clicked on while using shift. I.e, if user clicked index 0 (which is de-selected) to 10, then 0 -> 5,
 * while holding shift and never letting go, we should select 0 - 5 while deselecting 6-10.
 *
 * @param {Array<Object>} items List of items to select from.
 * @param {Function} onSelectItems Method to mark one or more items as selected. Must support multiple items toggled at once.
 * @param {Function} onDeselectItems Method to mark one or more items as de-selected. Must support multiple items toggled at once.
 */
const useShiftSelect = (items, onSelectItems, onDeselectItems) => {
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [baseIndex, setBaseIndex] = useState(undefined);
  const [lastIndex, setLastIndex] = useState(undefined);
  const [lastActionPerformed, setLastActionPerformed] = useState(null);

  useEffect(() => {
    if (!window) {
      return undefined;
    }

    const onShiftDown = event => {
      if (event.keyCode === SHIFT) {
        setIsShiftPressed(true);
      }
    };

    const onShiftUp = event => {
      if (event.keyCode === SHIFT) {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener('keydown', onShiftDown);
    window.addEventListener('keyup', onShiftUp);

    return () => {
      window.removeEventListener('keydown', onShiftDown);
      window.removeEventListener('keyup', onShiftUp);
    };
  }, []);

  const onToggleSelectedItem = (item, isSelected) => {
    const index = items.indexOf(item);

    if (index === -1) {
      return; // nothing to do here it seems
    }

    if (!isShiftPressed || typeof baseIndex === 'undefined') {
      setLastActionPerformed(
        isSelected ? ACTION_TYPES.DESELECT : ACTION_TYPES.SELECT,
      );

      if (isSelected) {
        onDeselectItems([item]);
      } else {
        onSelectItems([item]);
      }

      setBaseIndex(index);
      setLastIndex(undefined);
      return;
    }

    if (isShiftPressed && typeof baseIndex !== 'undefined') {
      // This means we've previously selected / deselected an item and we can now take action upon it.
      const to = baseIndex > index ? baseIndex : index;
      const from = index > baseIndex ? baseIndex : index;

      const itemsToUpdateWithLastAction = Array.from(
        { length: to - from + 1 },
        (x, i) => i + from,
      )
        .map(indexToUpdate => items[indexToUpdate])
        .filter(Boolean);

      const itemsToUpdateWithOppositeAction = [];

      if (typeof lastIndex !== 'undefined') {
        // Clean up after last selection holding shift.
        // This simulates shift behavior on standard OS:
        const toNext = to + 1;
        const cleanFrom = toNext > lastIndex ? lastIndex : toNext;
        const cleanTo = toNext > lastIndex ? toNext : lastIndex;

        itemsToUpdateWithOppositeAction.push(
          ...Array.from(
            { length: cleanTo - cleanFrom + 1 },
            (x, i) => i + cleanFrom,
          )
            .map(indexToUpdate => items[indexToUpdate])
            .filter(Boolean),
        );
      }

      if (lastActionPerformed === ACTION_TYPES.SELECT) {
        onDeselectItems(itemsToUpdateWithOppositeAction);
        onSelectItems(itemsToUpdateWithLastAction);
      } else {
        onSelectItems(itemsToUpdateWithOppositeAction);
        onDeselectItems(itemsToUpdateWithLastAction);
      }

      setLastIndex(index);
    }
  };

  return { onToggleSelectedItem };
};

export default useShiftSelect;
